import type { RootState } from "@/store"
import { useState, useEffect, useCallback } from "react"
import { Box, Typography, Button, Stack, useTheme } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import { capitalize } from "@/lib/formato"
import { useLazyGetContaTotemCheckoutQuery } from "@/api"
import { resetarView, trocarView, type MudancaKiosk } from "./navegacao-kiosk"
import AvisoInatividade from "@/components/totem/aviso-inatividade"
import StatusFeedback, { type FeedbackStatus } from "./components/status-feedback"
import { SobreCardapio } from "@/components/sobre-cardapio"
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined'

const MAX_DIGITS = 12
const DWELL_SUCESSO_MS = 2500
const INATIVIDADE_MS = 60_000

const buttonStyle = {
    height: '100%',
    width: '100%',
    fontSize: 'clamp(1.5rem, 5vmin, 2.5rem)',
    fontWeight: 600,
    backgroundColor: (theme: any) => theme.palette.personal?.header || '#333',
    color: 'inherit',
    borderRadius: '12px',
    boxShadow: 'none',
    textTransform: 'none',
}

function SearchIdentifier() {
    const dispatch = useDispatch()
    const theme = useTheme()
    const logoUrl = useSelector((state: RootState) => state.selfServiceKiosk.logoUrl)
    const modeloIdentificador = useSelector((state: RootState) => state.selfServiceKiosk.modeloIdentificador)
    const rotuloIdentificador = capitalize(modeloIdentificador)
    const [buscarConta] = useLazyGetContaTotemCheckoutQuery()
    const [identifier, setIdentifier] = useState('')
    const [status, setStatus] = useState<FeedbackStatus | null>(null)
    const [searchingIdentifier, setSearchingIdentifier] = useState('')
    const [destino, setDestino] = useState<MudancaKiosk | null>(null)
    const [qtdPedidos, setQtdPedidos] = useState(0)

    const fetchingOrder = status === 'loading'

    const handleNumberClick = useCallback((num: string) => {
        setIdentifier((prev) => (prev.length >= MAX_DIGITS ? prev : prev + num))
    }, [])

    const handleBackspace = useCallback(() => {
        setIdentifier((prev) => prev.slice(0, -1))
    }, [])

    const handleDismiss = useCallback(() => {
        setStatus(null)
        setIdentifier('')
        setSearchingIdentifier('')
    }, [])

    const handleConfirm = useCallback(async () => {
        if (!identifier) return

        try {
            setStatus('loading')
            setSearchingIdentifier(identifier)

            const [conta] = await Promise.all([
                buscarConta({ identificador: identifier }).unwrap(),
                new Promise((resolve) => setTimeout(resolve, 1500)),
            ])

            const pedidos = conta.pedidos ?? []

            if (!pedidos.length) {
                setStatus('x')
                return
            }

            setQtdPedidos(pedidos.length)
            setDestino(
                pedidos.length > 1
                    ? {
                        currentView: 'VIEW_MULTIPLE_ORDERS',
                        multipleOrders: pedidos,
                        hasMultipleOrders: true,
                        identificadorBuscado: conta.identificador,
                        emiteNfce: conta.emiteNfce ?? false,
                    }
                    : {
                        currentView: 'VIEW_ITEMS',
                        order: pedidos[0],
                        hasMultipleOrders: false,
                        identificadorBuscado: conta.identificador,
                        emiteNfce: conta.emiteNfce ?? false,
                    },
            )
            setStatus('sucesso')
        } catch (err) {
            console.error('Erro ao buscar pedido pelo identificador:', err)
            setStatus('alerta')
        }
    }, [identifier, buscarConta])

    useEffect(() => {
        if (status !== 'sucesso' || !destino) return

        const timer = setTimeout(() => trocarView(dispatch, destino), DWELL_SUCESSO_MS)
        return () => clearTimeout(timer)
    }, [status, destino, dispatch])

    const voltarAoInicio = useCallback(() => resetarView(dispatch), [dispatch])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (status) {
                if ((status === 'x' || status === 'alerta') && event.key === 'Enter') handleDismiss()
                return
            }

            if (event.key === 'Enter') {
                handleConfirm()
                return
            }

            if (event.key === 'Backspace') {
                handleBackspace()
                return
            }

            if (/^[a-zA-Z0-9]$/.test(event.key)) {
                handleNumberClick(event.key.toUpperCase())
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [status, handleConfirm, handleBackspace, handleNumberClick, handleDismiss])

    const tituloFeedback =
        status === 'loading'
            ? `Buscando sua ${rotuloIdentificador}...`
            : status === 'sucesso'
                ? `${rotuloIdentificador} ${searchingIdentifier} encontrada!`
                : status === 'x'
                    ? `${rotuloIdentificador} ${searchingIdentifier} não encontrada`
                    : 'Não foi possível buscar'

    const detalheFeedback =
        status === 'loading'
            ? `${rotuloIdentificador} Nº ${searchingIdentifier}`
            : status === 'sucesso'
                ? `${qtdPedidos} ${qtdPedidos === 1 ? 'pedido em aberto' : 'pedidos em aberto'}`
                : status === 'x'
                    ? `Não há pedidos em aberto para esta ${rotuloIdentificador.toLowerCase()}.`
                    : 'Tente novamente em alguns instantes. Se continuar, chame um atendente.'

    return (
        <Box sx={{
            height: '100dvh',
            p: 3,
            boxSizing: 'border-box',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            gap: 3,
        }}
        >
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Box
                        component="img"
                        src={logoUrl ? logoUrl : 'logo-sischef.svg'}
                        alt="Logo da Unidade"
                        sx={{
                            maxHeight: 150,
                            width: '100%',
                            objectFit: 'contain'
                        }}
                    />
                </Box>

                <Stack sx={{ alignItems: 'center', textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', fontSize: 'clamp(2rem, 5vmin, 3rem)', mb: 1 }}>
                        Informe sua {rotuloIdentificador}
                    </Typography>

                    <Typography variant="subtitle1" sx={{ fontSize: 'clamp(1rem, 2.5vmin, 1.315rem)' }}>
                        Digite o número ou escaneie o código de barras pelo leitor
                    </Typography>
                </Stack>
            </Box>

            <Box sx={{ width: '100%', flex: 1.3 }}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box
                        sx={{
                            p: 2,
                            mb: 3,
                            width: '100%',
                            display: 'flex',
                            minHeight: '110px',
                            overflow: 'hidden',
                            alignItems: 'center',
                            borderRadius: '12px',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.personal.header,
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: identifier ? '3rem' : 'clamp(1.2rem, 5vmin, 22px)',
                                fontWeight: identifier ? 600 : 400,
                                letterSpacing: identifier ? '3px' : 'normal',
                                wordBreak: 'break-all',
                                textAlign: 'center',
                                width: '100%'
                            }}
                        >
                            {identifier || `Número da ${rotuloIdentificador}`}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gridTemplateRows: 'repeat(4, 1fr)',
                            height: '100%',
                            gap: 1.5,
                        }}
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                            <Button
                                key={num}
                                variant="contained"
                                sx={buttonStyle}
                                disabled={fetchingOrder}
                                onClick={() => handleNumberClick(num.toString())}
                            >
                                {num}
                            </Button>
                        ))}

                        <Button
                            variant="contained"
                            sx={buttonStyle}
                            onClick={handleBackspace}
                            disabled={identifier.length === 0 || fetchingOrder}
                        >
                            <BackspaceOutlinedIcon fontSize="large" />
                        </Button>

                        <Button
                            variant="contained"
                            sx={buttonStyle}
                            disabled={fetchingOrder}
                            onClick={() => handleNumberClick('0')}
                        >
                            0
                        </Button>

                        <Button
                            variant="contained"
                            disabled={fetchingOrder || !identifier}
                            sx={{ ...buttonStyle, backgroundColor: 'primary.main' }}
                            onClick={handleConfirm}
                        >
                            <CheckOutlinedIcon fontSize="large" sx={{ color: 'white' }} />
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', flexShrink: 0 }}>
                <SobreCardapio posicao="embutido" />
            </Box>

            <AvisoInatividade
                ativo={status !== 'loading' && status !== 'sucesso'}
                msInatividade={INATIVIDADE_MS}
                aoExpirar={voltarAoInicio}
            />

            <StatusFeedback
                status={status}
                titulo={tituloFeedback}
                detalhe={detalheFeedback}
                textoBotao={status === 'sucesso' ? undefined : 'Tentar novamente'}
                onDismiss={status === 'sucesso' ? undefined : handleDismiss}
                autoRetornoMs={status === 'sucesso' ? null : 8000}
            />
        </Box>
    )
}

export default SearchIdentifier